/**
 * Lightweight on-device error logger.
 *
 * Why not Sentry yet?  Sentry requires:
 *   1. A DSN from sentry.io
 *   2. `yarn add @sentry/react @sentry/capacitor` (user has to run locally)
 *   3. Source-map upload pipeline
 *
 * Until those are wired in, this logger captures:
 *   - All `console.error` / `console.warn` calls
 *   - All `window.onerror` events
 *   - All unhandled promise rejections
 *
 * Errors are stored in `localStorage["fitjong_errors_v1"]` (newest first,
 * capped at 50 entries). The Debug screen in Settings reads + displays them.
 * Players can also tap "Copy logs" to share via email when reporting a bug.
 *
 * To upgrade to Sentry later, just call `Sentry.captureException(error)` in
 * the same hooks below.
 */

const STORAGE_KEY = "fitjong_errors_v1";
const MAX_ENTRIES = 50;

function readLog() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function writeLog(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch (_) {
    // Quota or private-mode — silently drop.
  }
}

// Patterns to IGNORE — noisy React/Radix dev-mode warnings that aren't
// actionable for end-users and would otherwise spam the Debug page.
const IGNORE_PATTERNS = [
  /Missing.*Description.*aria-describedby/i,
  /Warning: validateDOMNesting/i,
  /Warning: Each child in a list should have a unique "key"/i,
  /componentWillReceiveProps has been renamed/i,
  /componentWillMount has been renamed/i,
];

function shouldIgnore(message) {
  return IGNORE_PATTERNS.some((re) => re.test(message));
}

export function logError(source, args) {
  const message = (args || []).map((a) => {
    if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ""}`;
    if (typeof a === "object") {
      try { return JSON.stringify(a); } catch (_) { return String(a); }
    }
    return String(a);
  }).join(" ");
  if (shouldIgnore(message)) return;
  const entry = {
    ts: new Date().toISOString(),
    source,
    message: message.slice(0, 2000),
    ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
    url: typeof location !== "undefined" ? location.pathname : "",
  };
  const log = readLog();
  log.unshift(entry);
  writeLog(log);
}

export function getErrorLog() {
  return readLog();
}

export function clearErrorLog() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
}

let installed = false;
export function installErrorLogger() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Patch console.error + console.warn (don't break the originals).
  const origErr = console.error;
  const origWarn = console.warn;
  console.error = (...args) => { try { logError("console.error", args); } catch (_) {} origErr.apply(console, args); };
  console.warn = (...args) => { try { logError("console.warn", args); } catch (_) {} origWarn.apply(console, args); };

  window.addEventListener("error", (event) => {
    logError("window.error", [event.message, event.filename + ":" + event.lineno + ":" + event.colno, event.error]);
  });

  window.addEventListener("unhandledrejection", (event) => {
    logError("unhandledrejection", [event.reason]);
  });
}
